import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Calculate as CalculateIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { payrollApi } from '../../../api/payrollApi';
import { RootState } from '../../../store/store';
import {
  BankTemplateColumnDto,
  BankTemplateColumnSourceType,
  BankTemplateColumnSourceTypeLabels,
  IncomeTaxSlabDto,
  LabourWelfareFundDto,
  PayCycle,
  PayrollConfigurationDto,
  PayrollElementValueType,
  PayrollElementValueTypeLabels,
  PayrollFinancialConfigurationDto,
  PayrollGroupDto,
  PayrollGroupElementDto,
  PayrollProrationBasis,
  PayrollProrationBasisLabels,
  PayrollSettingDto,
  PayslipTemplateDto,
  PayslipDetailDto,
  SalaryComponentDto,
  SalaryComponentType,
  StateTaxSlabDto,
  TaxExemptionDto,
} from '../../../types/payrollTypes';
import PayslipTemplateRenderer from '../components/PayslipTemplateRenderer';

type TabValue = 'general' | 'groups' | 'taxes' | 'payslip' | 'bank' | 'module';

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createDefaultPayslipTemplates = (): PayslipTemplateDto[] => [
  {
    id: '11111111-1111-4111-8111-111111111111',
    name: 'Classic Detailed Payslip',
    headerTemplate: '{{agencyName}}\nPayroll advice for {{month}} {{year}}\nEmployee: {{employeeName}} ({{employeeCode}})\nDepartment: {{department}}\nDesignation: {{designation}}\nDate of Joining: {{dateOfJoining}}\nPAN: {{panNumber}}',
    bodyTemplate: 'Earnings\n{{earningsTable}}\n\nDeductions\n{{deductionsTable}}\n\nAttendance Summary\nScheduled Working Days: {{scheduledWorkingDays}}\nPresent Days: {{presentDays}}\nPaid Leave: {{paidLeaveDays}}\nUnpaid Leave: {{unpaidLeaveDays}}\nAbsent Days: {{absentDays}}\nLOP Days: {{lopDays}}\nPayable Days: {{payableDays}}\n\nSalary Summary\nGross Salary: {{grossSalary}}\nTotal Earnings: {{totalEarnings}}\nTotal Deductions: {{totalDeductions}}\nNet Salary: {{netSalary}}\nNet Pay In Words: {{netSalaryInWords}}',
    footerTemplate: 'Bank Account: {{bankAccountNumber}}\nIFSC: {{ifscCode}}\nGenerated by Evita HRMS. Authorized Signatory: {{authorizedSignatory}}',
    isDefault: true,
  },
  {
    id: '22222222-2222-4222-8222-222222222222',
    name: 'Attendance Focus Payslip',
    headerTemplate: '{{agencyName}}\nAttendance linked salary slip - {{month}} {{year}}\n{{employeeName}} | {{employeeCode}}\nPay Group: {{payrollGroup}}\nLocation: {{workLocation}}',
    bodyTemplate: 'Employee Summary\nDepartment: {{department}}\nDesignation: {{designation}}\nBank Account: {{bankAccountNumber}}\nIFSC: {{ifscCode}}\nPAN: {{panNumber}}\n\nSalary Components\n{{componentsTable}}\n\nAttendance and LOP Summary\nScheduled Working Days: {{scheduledWorkingDays}}\nPresent Days: {{presentDays}}\nPaid Leave Days: {{paidLeaveDays}}\nLeave Without Pay: {{unpaidLeaveDays}}\nAbsent Days: {{absentDays}}\nLoss of Pay Days: {{lopDays}}\nPayable Days: {{payableDays}}\nOvertime Hours: {{overtimeHours}}\n\nNet Pay: {{netSalary}}\nNet Pay In Words: {{netSalaryInWords}}',
    footerTemplate: 'For {{agencyName}}\n{{authorizedSignatory}}\nConfidential salary document. Attendance and LOP values are finalized from approved payroll inputs.',
    isDefault: false,
  },
  {
    id: '33333333-3333-4333-8333-333333333333',
    name: 'Executive Summary Payslip',
    headerTemplate: '{{agencyName}} | Payslip {{month}} {{year}}\n{{employeeName}} ({{employeeCode}}) | {{department}} | {{designation}}',
    bodyTemplate: 'Quick Salary Snapshot\nGross Salary: {{grossSalary}}\nTotal Earnings: {{totalEarnings}}\nTotal Deductions: {{totalDeductions}}\nPayable Days: {{payableDays}}\nLOP Days: {{lopDays}}\nNet Pay: {{netSalary}}\n\nComponent Summary\n{{componentsTable}}\n\nPayment Details\nBank Account: {{bankAccountNumber}}\nIFSC: {{ifscCode}}',
    footerTemplate: 'Generated by Evita HRMS on {{generatedDate}}. This is a system generated document.',
    isDefault: false,
  },
];

const createDefaultFinancial = (): PayrollFinancialConfigurationDto => ({
  financialYear: '',
  standardDeduction: 0,
  cessPercentage: 0,
  taxRegime: '',
});

const createDefaultConfiguration = (): PayrollConfigurationDto => ({
  groups: [],
  taxes: {
    financial: createDefaultFinancial(),
    incomeTaxSlabs: [],
    exemptions: [],
    labourWelfareFunds: [],
    stateTaxSlabs: [],
  },
  payslipTemplates: createDefaultPayslipTemplates(),
  bankTemplate: {
    id: createId(),
    name: 'Primary Bank Template',
    columns: [],
  },
  moduleConfiguration: {
    autoGeneratePayslip: false,
    enableBankAdviceExport: false,
    enableProration: true,
    prorationBasis: PayrollProrationBasis.CalendarDays,
    lockPayslipAfterPayroll: false,
    overtimeHourlyRate: 0,
    overtimeMultiplier: 1,
    defaultPayslipTemplateId: null,
    defaultBankTemplateId: null,
  },
});

const createDefaultSettings = (): PayrollSettingDto => ({
  id: '',
  payCycle: PayCycle.Monthly,
  salaryDay: 1,
  pfEnabled: false,
  esiEnabled: false,
  tdsEnabled: false,
  professionalTaxEnabled: false,
  configuration: createDefaultConfiguration(),
});

const createGroup = (index: number): PayrollGroupDto => ({
  id: createId(),
  name: '',
  description: '',
  formula: '',
  displayOrder: index + 1,
  isActive: true,
  computedValue: 0,
  elements: [],
});

const createGroupElement = (index: number): PayrollGroupElementDto => ({
  id: createId(),
  elementCode: '',
  elementName: '',
  valueType: PayrollElementValueType.Fixed,
  value: 0,
  formula: '',
  displayOrder: index + 1,
  isActive: true,
  computedValue: 0,
});

const createIncomeTaxSlab = (): IncomeTaxSlabDto => ({
  id: createId(),
  regime: 'New',
  incomeFrom: 0,
  incomeTo: null,
  taxPercentage: 0,
  surchargePercentage: null,
});

const createExemption = (): TaxExemptionDto => ({
  id: createId(),
  section: '',
  description: '',
  maximumAmount: 0,
});

const createLabourWelfareFund = (): LabourWelfareFundDto => ({
  id: createId(),
  stateCode: '',
  employeeContribution: 0,
  employerContribution: 0,
  effectiveFrom: null,
});

const createStateTaxSlab = (): StateTaxSlabDto => ({
  id: createId(),
  stateCode: '',
  amountFrom: 0,
  amountTo: null,
  taxAmount: 0,
});

const createPayslipTemplate = (): PayslipTemplateDto => ({
  id: createId(),
  name: '',
  headerTemplate: '',
  bodyTemplate: '',
  footerTemplate: '',
  isDefault: false,
});

const createBankColumn = (sourceType: BankTemplateColumnSourceType, index: number): BankTemplateColumnDto => ({
  id: createId(),
  columnName: '',
  sourceKey: '',
  sourceType,
  format: '',
  displayOrder: index + 1,
  isRequired: false,
});

interface PayslipTemplatePreviewSectionProps {
  detail: PayslipDetailDto;
  template: PayslipTemplateDto;
}

const PayslipTemplatePreviewSection = React.memo<PayslipTemplatePreviewSectionProps>(({ detail, template }) => (
  <Box>
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
      Template Preview
    </Typography>
    <Paper
      variant="outlined"
      sx={{
        bgcolor: '#f6f8fb',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          maxHeight: { xs: 520, md: 620 },
          overflow: 'auto',
        }}
      >
        <PayslipTemplateRenderer detail={{ ...detail, template }} template={template} />
      </Box>
    </Paper>
  </Box>
));

const numberValue = (value: string) => {
  if (value === '') {
    return 0;
  }

  return Number(value);
};

const normalizeCode = (value: string) => value.trim().toLowerCase();

const PayrollSettingsPage: React.FC = () => {
  const tenantUser = useSelector((state: RootState) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [tab, setTab] = useState<TabValue>('general');
  const [settings, setSettings] = useState<PayrollSettingDto>(createDefaultSettings());
  const [salaryComponents, setSalaryComponents] = useState<SalaryComponentDto[]>([]);
  const [assignedGroupIds, setAssignedGroupIds] = useState<Set<string>>(new Set());
  const [editingPayslipTemplateId, setEditingPayslipTemplateId] = useState<string | null>(null);

  const configuration = settings.configuration ?? createDefaultConfiguration();
  const editingPayslipTemplate = useMemo(
    () => configuration.payslipTemplates.find((template) => template.id === editingPayslipTemplateId) ?? null,
    [configuration.payslipTemplates, editingPayslipTemplateId]
  );
  const previewDetail = useMemo<PayslipDetailDto>(() => ({
    payslipId: 'preview',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    generatedAt: new Date().toISOString(),
    employeeId: 'preview-employee',
    employeeName: 'Aarav Sharma',
    employeeCode: 'EMP-1024',
    companyName: tenantUser?.companyName || 'Tenant Company',
    companyAddress: 'Tenant configured address',
    department: 'Product Engineering',
    designation: 'Senior Software Engineer',
    dateOfJoining: '2023-01-12T00:00:00.000Z',
    panNumber: 'ABCDE1234F',
    officialEmail: 'aarav.sharma@example.com',
    workLocation: 'Bengaluru',
    bankAccountNumber: 'XXXX XXXX 4821',
    ifscCode: 'HDFC0001234',
    workingDays: 25,
    scheduledWorkingDays: 26,
    grossSalary: 86000,
    totalEarnings: 92500,
    totalDeductions: 8750,
    netSalary: 83750,
    presentDays: 23,
    leaveDays: 2,
    paidLeaveDays: 2,
    unpaidLeaveDays: 0,
    absentDays: 0,
    lopDays: 1,
    payableDays: 25,
    overtimeAmount: 7000,
    overtimeHours: 6,
    authorizedSignatory: 'Payroll Manager',
    payrollGroup: 'Monthly Staff',
    components: [
      { componentName: 'Basic Salary', type: SalaryComponentType.Earning, amount: 45000 },
      { componentName: 'House Rent Allowance', type: SalaryComponentType.Allowance, amount: 18000 },
      { componentName: 'Special Allowance', type: SalaryComponentType.Allowance, amount: 22500 },
      { componentName: 'Overtime', type: SalaryComponentType.Overtime, amount: 7000 },
      { componentName: 'Provident Fund', type: SalaryComponentType.Deduction, amount: 5400 },
      { componentName: 'Professional Tax', type: SalaryComponentType.TaxComponent, amount: 200 },
      { componentName: 'TDS', type: SalaryComponentType.TaxComponent, amount: 3150 },
    ],
  }), [tenantUser?.companyName]);

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const [data, components] = await Promise.all([
          payrollApi.getSettings(),
          payrollApi.getSalaryComponents(),
        ]);
        setSalaryComponents(components);
        if (data) {
          const componentByCode = new Map(components.map((component) => [normalizeCode(component.name), component]));
          const savedConfiguration = data.configuration ?? createDefaultConfiguration();
          const groups = (savedConfiguration.groups ?? []).map((group) => ({
            ...group,
            elements: (group.elements ?? []).map((element) => {
              const component = componentByCode.get(normalizeCode(element.elementCode));
              return component
                ? { ...element, elementCode: component.name, elementName: component.label }
                : element;
            }),
          }));

          setSettings({
            ...createDefaultSettings(),
            ...data,
            configuration: {
              ...createDefaultConfiguration(),
              ...savedConfiguration,
              groups,
              moduleConfiguration: {
                ...createDefaultConfiguration().moduleConfiguration,
                ...savedConfiguration.moduleConfiguration,
              },
              taxes: {
                ...createDefaultConfiguration().taxes,
                ...savedConfiguration.taxes,
                financial: {
                  ...createDefaultFinancial(),
                  ...savedConfiguration.taxes?.financial,
                },
              },
              bankTemplate: savedConfiguration.bankTemplate ?? createDefaultConfiguration().bankTemplate,
            },
          });
        }

        try {
          const assignmentOptions = await payrollApi.getGroupAssignmentOptions();
          setAssignedGroupIds(new Set(
            assignmentOptions.employees
              .map((employee) => employee.assignedGroupId)
              .filter((id): id is string => !!id)
          ));
        } catch {
          setAssignedGroupIds(new Set());
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || 'Failed to load payroll settings');
      } finally {
        setLoading(false);
      }
    };

    void loadSettings();
  }, []);

  const salaryComponentOptions = useMemo(
    () => [...salaryComponents].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [salaryComponents]
  );

  const salaryComponentByCode = useMemo(
    () => new Map(salaryComponents.map((component) => [normalizeCode(component.name), component])),
    [salaryComponents]
  );

  const getAvailableElementOptions = (group: PayrollGroupDto, currentElementIndex: number) => {
    const usedCodes = new Set(
      group.elements
        .filter((_, index) => index !== currentElementIndex)
        .map((element) => normalizeCode(element.elementCode))
        .filter(Boolean)
    );

    return salaryComponentOptions.filter((component) => !usedCodes.has(normalizeCode(component.name)));
  };

  const selectGroupElementCode = (groupIndex: number, elementIndex: number, selectedCode: string) => {
    const component = salaryComponentByCode.get(normalizeCode(selectedCode));

    updateConfiguration({
      ...configuration,
      groups: configuration.groups.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              elements: group.elements.map((element, childIndex) =>
                childIndex === elementIndex
                  ? {
                      ...element,
                      elementCode: component?.name ?? '',
                      elementName: component?.label ?? '',
                    }
                  : element
              ),
            }
          : group
      ),
    });
  };

  const validateGroups = (targetGroupIndex?: number) => {
    const groupsToValidate = targetGroupIndex === undefined
      ? configuration.groups
      : configuration.groups.filter((_, index) => index === targetGroupIndex);

    const duplicateGroup = targetGroupIndex === undefined
      ? configuration.groups
          .map((group) => group.name.trim())
          .find((name, index, names) => name && names.findIndex((item) => item.toLowerCase() === name.toLowerCase()) !== index)
      : '';

    if (duplicateGroup) {
      toast.error(`Duplicate group name '${duplicateGroup}' is not allowed`);
      return false;
    }

    for (const group of groupsToValidate) {
      const groupName = group.name.trim() || 'Payroll group';
      if (!group.name.trim()) {
        toast.error('Group name is required');
        return false;
      }

      const selectedCodes = group.elements.map((element) => normalizeCode(element.elementCode)).filter(Boolean);
      const duplicateCode = selectedCodes.find((code, index) => selectedCodes.indexOf(code) !== index);
      if (duplicateCode) {
        toast.error(`Duplicate element code '${duplicateCode}' is not allowed in ${groupName}`);
        return false;
      }

      for (const element of group.elements) {
        if (!element.elementCode.trim()) {
          toast.error(`Select an element code for ${groupName}`);
          return false;
        }

        const component = salaryComponentByCode.get(normalizeCode(element.elementCode));
        if (!component) {
          toast.error(`Element code '${element.elementCode}' is not available in salary components`);
          return false;
        }

        if (element.elementName !== component.label) {
          toast.error(`Element name for '${element.elementCode}' must match '${component.label}'`);
          return false;
        }

        if (element.valueType === PayrollElementValueType.Computed && !element.formula?.trim()) {
          toast.error(`Formula is required for computed element '${element.elementCode}'`);
          return false;
        }

        if (element.valueType === PayrollElementValueType.Fixed && Number(element.value ?? 0) < 0) {
          toast.error(`Value for '${element.elementCode}' cannot be negative`);
          return false;
        }
      }
    }

    return true;
  };

  const saveGeneralSettings = async () => {
    setSaving('general');
    try {
      await payrollApi.saveSettings({
        payCycle: settings.payCycle,
        salaryDay: settings.salaryDay,
        pfEnabled: settings.pfEnabled,
        esiEnabled: settings.esiEnabled,
        tdsEnabled: settings.tdsEnabled,
        professionalTaxEnabled: settings.professionalTaxEnabled,
      });
      toast.success('Payroll settings saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save payroll settings');
    } finally {
      setSaving(null);
    }
  };

  const saveGroups = async () => {
    if (!validateGroups()) return;

    setSaving('groups');
    try {
      const response = await payrollApi.saveGroups(configuration.groups);
      const groups = await payrollApi.getGroups();
      setSettings((prev) => ({
        ...prev,
        id: prev.id || response.id,
        configuration: {
          ...prev.configuration,
          groups,
        },
      }));
      toast.success('Payroll groups saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save payroll groups');
    } finally {
      setSaving(null);
    }
  };

  const saveTaxes = async () => {
    setSaving('taxes');
    try {
      await payrollApi.saveTaxes(configuration.taxes);
      toast.success('Tax configuration saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save tax configuration');
    } finally {
      setSaving(null);
    }
  };

  const savePayslipTemplates = async () => {
    setSaving('payslip');
    try {
      await payrollApi.savePayslipTemplates(configuration.payslipTemplates);
      toast.success('Payslip templates saved');
      return true;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save payslip templates');
      return false;
    } finally {
      setSaving(null);
    }
  };

  const savePayslipTemplateEditor = async () => {
    const saved = await savePayslipTemplates();
    if (saved) {
      setEditingPayslipTemplateId(null);
    }
  };

  const saveBankTemplate = async () => {
    setSaving('bank');
    try {
      await payrollApi.saveBankTemplate(configuration.bankTemplate ?? {
        id: createId(),
        name: 'Primary Bank Template',
        columns: [],
      });
      toast.success('Bank template saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save bank template');
    } finally {
      setSaving(null);
    }
  };

  const saveModuleConfiguration = async () => {
    setSaving('module');
    try {
      await payrollApi.saveModuleConfiguration(configuration.moduleConfiguration);
      toast.success('Payroll module configuration saved');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save payroll module configuration');
    } finally {
      setSaving(null);
    }
  };

  const updateConfiguration = (nextConfiguration: PayrollConfigurationDto) => {
    setSettings((prev) => ({
      ...prev,
      configuration: nextConfiguration,
    }));
  };

  const updatePayslipTemplate = (templateId: string, changes: Partial<PayslipTemplateDto>) => {
    updateConfiguration({
      ...configuration,
      payslipTemplates: configuration.payslipTemplates.map((template) =>
        template.id === templateId ? { ...template, ...changes } : template
      ),
    });
  };

  const setDefaultPayslipTemplate = (templateId: string, isDefault: boolean) => {
    updateConfiguration({
      ...configuration,
      moduleConfiguration: {
        ...configuration.moduleConfiguration,
        defaultPayslipTemplateId: isDefault ? templateId : null,
      },
      payslipTemplates: configuration.payslipTemplates.map((template) => ({
        ...template,
        isDefault: template.id === templateId ? isDefault : false,
      })),
    });
  };

  const addPayslipTemplate = () => {
    const template = createPayslipTemplate();
    updateConfiguration({
      ...configuration,
      payslipTemplates: [...configuration.payslipTemplates, template],
    });
    setEditingPayslipTemplateId(template.id);
  };

  const removePayslipTemplate = (templateId: string) => {
    updateConfiguration({
      ...configuration,
      payslipTemplates: configuration.payslipTemplates.filter((template) => template.id !== templateId),
    });

    if (editingPayslipTemplateId === templateId) {
      setEditingPayslipTemplateId(null);
    }
  };

  const activeTemplateOptions = useMemo(
    () => configuration.payslipTemplates.filter((item) => item.name.trim() !== ''),
    [configuration.payslipTemplates]
  );

  const addSystemColumns = async () => {
    try {
      const systemColumns = await payrollApi.getBankTemplateSystemColumns();
      const currentTemplate = configuration.bankTemplate ?? {
        id: createId(),
        name: 'Primary Bank Template',
        columns: [],
      };

      const existingKeys = new Set(currentTemplate.columns.map((column) => column.sourceKey.toLowerCase()));
      const mergedColumns = [
        ...currentTemplate.columns,
        ...systemColumns
          .filter((column) => !existingKeys.has(column.sourceKey.toLowerCase()))
          .map((column, index) => ({
            ...column,
            id: column.id || createId(),
            displayOrder: currentTemplate.columns.length + index + 1,
          })),
      ];

      updateConfiguration({
        ...configuration,
        bankTemplate: {
          ...currentTemplate,
          columns: mergedColumns,
        },
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load system columns');
    }
  };

  const previewGroup = async (groupIndex: number) => {
    if (!validateGroups(groupIndex)) return;

    const group = configuration.groups[groupIndex];
    try {
      const preview = await payrollApi.calculateGroup(group);
      updateConfiguration({
        ...configuration,
        groups: configuration.groups.map((item, index) =>
          index === groupIndex
            ? {
                ...item,
                computedValue: preview.computedValue,
                elements: preview.elements,
              }
            : item
        ),
      });
      toast.success(`Calculated value: ${preview.computedValue}`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to calculate payroll group');
    }
  };

  const removeGroup = (groupIndex: number) => {
    const group = configuration.groups[groupIndex];
    if (group?.id && assignedGroupIds.has(group.id)) {
      toast.error('This payroll group is assigned to employee(s) and cannot be deleted.');
      return;
    }

    updateConfiguration({ ...configuration, groups: configuration.groups.filter((_, index) => index !== groupIndex) });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Payroll Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure statutory settings, payroll groups, taxes, payslip templates, bank exports, and module behavior.
          </Typography>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, value: TabValue) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="General" value="general" sx={{ textTransform: 'none' }} />
            <Tab label="Groups" value="groups" sx={{ textTransform: 'none' }} />
            <Tab label="Taxes" value="taxes" sx={{ textTransform: 'none' }} />
            <Tab label="Payslip Template" value="payslip" sx={{ textTransform: 'none' }} />
            <Tab label="Bank Template" value="bank" sx={{ textTransform: 'none' }} />
            <Tab label="Module Config" value="module" sx={{ textTransform: 'none' }} />
          </Tabs>
        </Paper>

        {tab === 'general' && (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    label="Pay Cycle"
                    fullWidth
                    value={settings.payCycle}
                    onChange={(event) => setSettings((prev) => ({ ...prev, payCycle: Number(event.target.value) as PayCycle }))}
                  >
                    <MenuItem value={PayCycle.Monthly}>Monthly</MenuItem>
                    <MenuItem value={PayCycle.Weekly}>Weekly</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Salary Day"
                    type="number"
                    fullWidth
                    value={settings.salaryDay}
                    inputProps={{ min: 1, max: 31 }}
                    onChange={(event) => setSettings((prev) => ({ ...prev, salaryDay: numberValue(event.target.value) }))}
                  />
                </Grid>
              </Grid>

              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveGeneralSettings} disabled={saving === 'general'} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                {saving === 'general' ? 'Saving...' : 'Save General Settings'}
              </Button>
            </Stack>
          </Paper>
        )}

        {tab === 'groups' && (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Add Group Form</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => updateConfiguration({ ...configuration, groups: [...configuration.groups, createGroup(configuration.groups.length)] })}
                >
                  Add Group
                </Button>
              </Stack>

              {configuration.groups.map((group, groupIndex) => (
                <Paper key={group.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={600}>Group {groupIndex + 1}</Typography>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" startIcon={<CalculateIcon />} onClick={() => void previewGroup(groupIndex)}>
                          Preview
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => removeGroup(groupIndex)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label="Group Name"
                          fullWidth
                          value={group.name}
                          onChange={(event) => updateConfiguration({
                            ...configuration,
                            groups: configuration.groups.map((item, index) => index === groupIndex ? { ...item, name: event.target.value } : item),
                          })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label="Formula"
                          fullWidth
                          value={group.formula ?? ''}
                          onChange={(event) => updateConfiguration({
                            ...configuration,
                            groups: configuration.groups.map((item, index) => index === groupIndex ? { ...item, formula: event.target.value } : item),
                          })}
                          helperText="Example: BASIC + HRA"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField label="Computed Value" fullWidth value={group.computedValue} InputProps={{ readOnly: true }} />
                      </Grid>
                      <Grid size={12}>
                        <TextField
                          label="Description"
                          fullWidth
                          value={group.description ?? ''}
                          onChange={(event) => updateConfiguration({
                            ...configuration,
                            groups: configuration.groups.map((item, index) => index === groupIndex ? { ...item, description: event.target.value } : item),
                          })}
                        />
                      </Grid>
                    </Grid>

                    <Divider />

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">Elements</Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        disabled={getAvailableElementOptions(group, -1).length === 0}
                        onClick={() => updateConfiguration({
                          ...configuration,
                          groups: configuration.groups.map((item, index) =>
                            index === groupIndex
                              ? { ...item, elements: [...item.elements, createGroupElement(item.elements.length)] }
                              : item
                          ),
                        })}
                      >
                        Add Element
                      </Button>
                    </Stack>

                    {salaryComponentOptions.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Create salary components first, then use them here as group elements.
                      </Typography>
                    )}

                    <Stack spacing={2}>
                      {group.elements.map((element, elementIndex) => (
                        <Box
                          key={element.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              md: 'repeat(2, minmax(0, 1fr))',
                              lg: 'minmax(0, 1.4fr) minmax(0, 1.4fr) minmax(0, 0.95fr) minmax(0, 1fr) minmax(96px, 0.65fr) 40px',
                            },
                            gap: 2,
                            alignItems: 'start',
                          }}
                        >
                          <TextField
                            select
                            label="Code"
                            fullWidth
                            value={element.elementCode}
                            onChange={(event) => selectGroupElementCode(groupIndex, elementIndex, event.target.value)}
                            helperText={element.elementCode ? 'Element code from salary components' : 'Select created element'}
                          >
                            <MenuItem value="">Select Element Code</MenuItem>
                            {getAvailableElementOptions(group, elementIndex).map((component) => (
                              <MenuItem key={component.id} value={component.name} disabled={!component.isActive}>
                                {component.name}{component.isActive ? '' : ' (Inactive)'}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            label="Name"
                            fullWidth
                            value={element.elementName}
                            InputProps={{ readOnly: true }}
                            helperText={element.elementName ? 'Auto-filled from selected code' : 'Name will bind automatically'}
                          />
                          <TextField
                            select
                            label="Type"
                            fullWidth
                            value={element.valueType}
                            helperText=" "
                            onChange={(event) => updateConfiguration({
                              ...configuration,
                              groups: configuration.groups.map((item, index) =>
                                index === groupIndex
                                  ? {
                                      ...item,
                                      elements: item.elements.map((child, childIndex) =>
                                        childIndex === elementIndex
                                          ? { ...child, valueType: Number(event.target.value) as PayrollElementValueType }
                                          : child
                                      ),
                                    }
                                  : item
                              ),
                            })}
                          >
                            <MenuItem value={PayrollElementValueType.Fixed}>{PayrollElementValueTypeLabels[PayrollElementValueType.Fixed]}</MenuItem>
                            <MenuItem value={PayrollElementValueType.Computed}>{PayrollElementValueTypeLabels[PayrollElementValueType.Computed]}</MenuItem>
                          </TextField>
                          {element.valueType === PayrollElementValueType.Fixed ? (
                            <TextField
                              label="Value"
                              type="number"
                              fullWidth
                              value={element.value ?? 0}
                              onChange={(event) => updateConfiguration({
                                ...configuration,
                                groups: configuration.groups.map((item, index) =>
                                  index === groupIndex
                                    ? {
                                        ...item,
                                        elements: item.elements.map((child, childIndex) =>
                                          childIndex === elementIndex ? { ...child, value: numberValue(event.target.value) } : child
                                        ),
                                      }
                                    : item
                                ),
                              })}
                              helperText=" "
                            />
                          ) : (
                            <TextField
                              label="Formula"
                              fullWidth
                              value={element.formula ?? ''}
                              onChange={(event) => updateConfiguration({
                                ...configuration,
                                groups: configuration.groups.map((item, index) =>
                                  index === groupIndex
                                    ? {
                                        ...item,
                                        elements: item.elements.map((child, childIndex) =>
                                          childIndex === elementIndex ? { ...child, formula: event.target.value } : child
                                        ),
                                      }
                                    : item
                                ),
                              })}
                              helperText=" "
                            />
                          )}
                          <TextField label="Computed" fullWidth value={element.computedValue} InputProps={{ readOnly: true }} helperText=" " />
                          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', lg: 'center' }, pt: { xs: 0, lg: 1 } }}>
                            <IconButton
                              color="error"
                              onClick={() => updateConfiguration({
                                ...configuration,
                                groups: configuration.groups.map((item, index) =>
                                  index === groupIndex
                                    ? { ...item, elements: item.elements.filter((_, childIndex) => childIndex !== elementIndex) }
                                    : item
                                ),
                              })}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              ))}

              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveGroups} disabled={saving === 'groups'} sx={{ alignSelf: 'flex-start' , textTransform: 'none' }}>
                {saving === 'groups' ? 'Saving...' : 'Save Groups'}
              </Button>
            </Stack>
          </Paper>
        )}

        {tab === 'taxes' && (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Typography variant="h6">Taxes Form</Typography>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight={600} mb={2}>Financial Tab</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Financial Year"
                      fullWidth
                      value={configuration.taxes.financial.financialYear}
                      onChange={(event) => updateConfiguration({
                        ...configuration,
                        taxes: {
                          ...configuration.taxes,
                          financial: { ...configuration.taxes.financial, financialYear: event.target.value },
                        },
                      })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Standard Deduction"
                      type="number"
                      fullWidth
                      value={configuration.taxes.financial.standardDeduction}
                      onChange={(event) => updateConfiguration({
                        ...configuration,
                        taxes: {
                          ...configuration.taxes,
                          financial: { ...configuration.taxes.financial, standardDeduction: numberValue(event.target.value) },
                        },
                      })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Cess %"
                      type="number"
                      fullWidth
                      value={configuration.taxes.financial.cessPercentage}
                      onChange={(event) => updateConfiguration({
                        ...configuration,
                        taxes: {
                          ...configuration.taxes,
                          financial: { ...configuration.taxes.financial, cessPercentage: numberValue(event.target.value) },
                        },
                      })}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography fontWeight={600}>Income Tax Slab Tab</Typography>
                  <Button startIcon={<AddIcon />} onClick={() => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, incomeTaxSlabs: [...configuration.taxes.incomeTaxSlabs, createIncomeTaxSlab()] } })}>
                    Add Slab
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  {configuration.taxes.incomeTaxSlabs.map((slab, index) => (
                    <Grid key={slab.id} container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField label="Regime" fullWidth value={slab.regime} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, incomeTaxSlabs: configuration.taxes.incomeTaxSlabs.map((item, itemIndex) => itemIndex === index ? { ...item, regime: event.target.value } : item) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField label="From" type="number" fullWidth value={slab.incomeFrom} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, incomeTaxSlabs: configuration.taxes.incomeTaxSlabs.map((item, itemIndex) => itemIndex === index ? { ...item, incomeFrom: numberValue(event.target.value) } : item) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField label="To" type="number" fullWidth value={slab.incomeTo ?? ''} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, incomeTaxSlabs: configuration.taxes.incomeTaxSlabs.map((item, itemIndex) => itemIndex === index ? { ...item, incomeTo: event.target.value === '' ? null : numberValue(event.target.value) } : item) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField label="Tax %" type="number" fullWidth value={slab.taxPercentage} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, incomeTaxSlabs: configuration.taxes.incomeTaxSlabs.map((item, itemIndex) => itemIndex === index ? { ...item, taxPercentage: numberValue(event.target.value) } : item) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Surcharge %" type="number" fullWidth value={slab.surchargePercentage ?? ''} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, incomeTaxSlabs: configuration.taxes.incomeTaxSlabs.map((item, itemIndex) => itemIndex === index ? { ...item, surchargePercentage: event.target.value === '' ? null : numberValue(event.target.value) } : item) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <IconButton color="error" onClick={() => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, incomeTaxSlabs: configuration.taxes.incomeTaxSlabs.filter((_, itemIndex) => itemIndex !== index) } })}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography fontWeight={600}>Exemption Tab</Typography>
                  <Button startIcon={<AddIcon />} onClick={() => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, exemptions: [...configuration.taxes.exemptions, createExemption()] } })}>
                    Add Exemption
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  {configuration.taxes.exemptions.map((item, index) => (
                    <Grid key={item.id} container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Section" fullWidth value={item.section} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, exemptions: configuration.taxes.exemptions.map((row, rowIndex) => rowIndex === index ? { ...row, section: event.target.value } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 5 }}>
                        <TextField label="Description" fullWidth value={item.description ?? ''} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, exemptions: configuration.taxes.exemptions.map((row, rowIndex) => rowIndex === index ? { ...row, description: event.target.value } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Max Amount" type="number" fullWidth value={item.maximumAmount} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, exemptions: configuration.taxes.exemptions.map((row, rowIndex) => rowIndex === index ? { ...row, maximumAmount: numberValue(event.target.value) } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <IconButton color="error" onClick={() => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, exemptions: configuration.taxes.exemptions.filter((_, rowIndex) => rowIndex !== index) } })}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography fontWeight={600}>Labour Welfare Fund Tab</Typography>
                  <Button startIcon={<AddIcon />} onClick={() => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, labourWelfareFunds: [...configuration.taxes.labourWelfareFunds, createLabourWelfareFund()] } })}>
                    Add LWF
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  {configuration.taxes.labourWelfareFunds.map((item, index) => (
                    <Grid key={item.id} container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField label="State" fullWidth value={item.stateCode} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, labourWelfareFunds: configuration.taxes.labourWelfareFunds.map((row, rowIndex) => rowIndex === index ? { ...row, stateCode: event.target.value } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Employee Contribution" type="number" fullWidth value={item.employeeContribution} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, labourWelfareFunds: configuration.taxes.labourWelfareFunds.map((row, rowIndex) => rowIndex === index ? { ...row, employeeContribution: numberValue(event.target.value) } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Employer Contribution" type="number" fullWidth value={item.employerContribution} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, labourWelfareFunds: configuration.taxes.labourWelfareFunds.map((row, rowIndex) => rowIndex === index ? { ...row, employerContribution: numberValue(event.target.value) } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Effective From" type="date" fullWidth InputLabelProps={{ shrink: true }} value={item.effectiveFrom ?? ''} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, labourWelfareFunds: configuration.taxes.labourWelfareFunds.map((row, rowIndex) => rowIndex === index ? { ...row, effectiveFrom: event.target.value || null } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <IconButton color="error" onClick={() => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, labourWelfareFunds: configuration.taxes.labourWelfareFunds.filter((_, rowIndex) => rowIndex !== index) } })}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography fontWeight={600}>State Tax Slabs Tab</Typography>
                  <Button startIcon={<AddIcon />} onClick={() => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, stateTaxSlabs: [...configuration.taxes.stateTaxSlabs, createStateTaxSlab()] } })}>
                    Add State Slab
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  {configuration.taxes.stateTaxSlabs.map((item, index) => (
                    <Grid key={item.id} container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, md: 2 }}>
                        <TextField label="State" fullWidth value={item.stateCode} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, stateTaxSlabs: configuration.taxes.stateTaxSlabs.map((row, rowIndex) => rowIndex === index ? { ...row, stateCode: event.target.value } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Amount From" type="number" fullWidth value={item.amountFrom} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, stateTaxSlabs: configuration.taxes.stateTaxSlabs.map((row, rowIndex) => rowIndex === index ? { ...row, amountFrom: numberValue(event.target.value) } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Amount To" type="number" fullWidth value={item.amountTo ?? ''} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, stateTaxSlabs: configuration.taxes.stateTaxSlabs.map((row, rowIndex) => rowIndex === index ? { ...row, amountTo: event.target.value === '' ? null : numberValue(event.target.value) } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField label="Tax Amount" type="number" fullWidth value={item.taxAmount} onChange={(event) => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, stateTaxSlabs: configuration.taxes.stateTaxSlabs.map((row, rowIndex) => rowIndex === index ? { ...row, taxAmount: numberValue(event.target.value) } : row) } })} />
                      </Grid>
                      <Grid size={{ xs: 12, md: 1 }}>
                        <IconButton color="error" onClick={() => updateConfiguration({ ...configuration, taxes: { ...configuration.taxes, stateTaxSlabs: configuration.taxes.stateTaxSlabs.filter((_, rowIndex) => rowIndex !== index) } })}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </Paper>

              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveTaxes} disabled={saving === 'taxes'} sx={{ alignSelf: 'flex-start' , textTransform: 'none' }}>
                {saving === 'taxes' ? 'Saving...' : 'Save Taxes'}
              </Button>
            </Stack>
          </Paper>
        )}

        {tab === 'payslip' && (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5}>
                <Box>
                  <Typography variant="h6">Payslip Templates</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    <Chip size="small" label={`${configuration.payslipTemplates.length} templates`} />
                    <Chip size="small" color="primary" variant="outlined" label={configuration.payslipTemplates.find((item) => item.isDefault)?.name || 'No default'} />
                  </Stack>
                </Box>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addPayslipTemplate} sx={{ textTransform: 'none' }}>
                  Add Template
                </Button>
              </Stack>

              {configuration.payslipTemplates.map((template, index) => (
                <Paper key={template.id} variant="outlined" sx={{ p: 2, borderRadius: 1.5, borderColor: template.isDefault ? 'primary.main' : 'divider' }}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography fontWeight={700}>{template.name || `Template ${index + 1}`}</Typography>
                          {template.isDefault && <Chip size="small" color="primary" label="Default" />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {template.headerTemplate.trim() ? template.headerTemplate.trim().split('\n')[0] : 'No header configured'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                        <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditingPayslipTemplateId(template.id)} sx={{ textTransform: 'none' }}>
                          Edit
                        </Button>
                        <IconButton color="error" aria-label={`Delete ${template.name || `Template ${index + 1}`}`} onClick={() => removePayslipTemplate(template.id)} sx={{ textTransform: 'none' }}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <PayslipTemplatePreviewSection detail={previewDetail} template={template} />
                  </Stack>
                </Paper>
              ))}

              <Button variant="contained" startIcon={<SaveIcon />} onClick={savePayslipTemplates} disabled={saving === 'payslip'} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                {saving === 'payslip' ? 'Saving...' : 'Save Payslip Templates'}
              </Button>
            </Stack>
          </Paper>
        )}

        <Dialog open={Boolean(editingPayslipTemplate)} onClose={() => setEditingPayslipTemplateId(null)} maxWidth="md" fullWidth>
          <DialogTitle>{editingPayslipTemplate?.name ? `Edit ${editingPayslipTemplate.name}` : 'Edit Payslip Template'}</DialogTitle>
          <DialogContent dividers>
            {editingPayslipTemplate && (
              <Stack spacing={2} sx={{ pt: 0.5 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <TextField
                      label="Template Name"
                      fullWidth
                      value={editingPayslipTemplate.name}
                      onChange={(event) => updatePayslipTemplate(editingPayslipTemplate.id, { name: event.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editingPayslipTemplate.isDefault}
                          onChange={(event) => setDefaultPayslipTemplate(editingPayslipTemplate.id, event.target.checked)}
                        />
                      }
                      label="Default Template"
                    />
                  </Grid>
                  <Grid size={12}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {['agencyName', 'employeeName', 'employeeCode', 'month', 'year', 'earningsTable', 'deductionsTable', 'netSalary'].map((token) => (
                        <Chip key={token} size="small" variant="outlined" label={`{{${token}}}`} />
                      ))}
                    </Stack>
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label="Header Template"
                      fullWidth
                      multiline
                      minRows={4}
                      value={editingPayslipTemplate.headerTemplate}
                      onChange={(event) => updatePayslipTemplate(editingPayslipTemplate.id, { headerTemplate: event.target.value })}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label="Body Template"
                      fullWidth
                      multiline
                      minRows={8}
                      value={editingPayslipTemplate.bodyTemplate}
                      onChange={(event) => updatePayslipTemplate(editingPayslipTemplate.id, { bodyTemplate: event.target.value })}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label="Footer Template"
                      fullWidth
                      multiline
                      minRows={3}
                      value={editingPayslipTemplate.footerTemplate}
                      onChange={(event) => updatePayslipTemplate(editingPayslipTemplate.id, { footerTemplate: event.target.value })}
                    />
                  </Grid>
                </Grid>

                <PayslipTemplatePreviewSection detail={previewDetail} template={editingPayslipTemplate} />
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingPayslipTemplateId(null)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={savePayslipTemplateEditor}
              disabled={saving === 'payslip'}
              sx={{ textTransform: 'none' }}
            >
              {saving === 'payslip' ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogActions>
        </Dialog>

        {tab === 'bank' && (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Bank Template Form</Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={() => void addSystemColumns()} sx={{ textTransform: 'none' }}>
                    Add System Columns
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => updateConfiguration({
                      ...configuration,
                      bankTemplate: {
                        ...(configuration.bankTemplate ?? { id: createId(), name: 'Primary Bank Template', columns: [] }),
                        columns: [...(configuration.bankTemplate?.columns ?? []), createBankColumn(BankTemplateColumnSourceType.Custom, configuration.bankTemplate?.columns.length ?? 0)],
                      },
                    })}
                    sx={{ textTransform: 'none' }}
                  >
                    Add Custom Column
                  </Button>
                </Stack>
              </Stack>

              <TextField
                label="Template Name"
                fullWidth
                value={configuration.bankTemplate?.name ?? ''}
                onChange={(event) => updateConfiguration({
                  ...configuration,
                  bankTemplate: {
                    ...(configuration.bankTemplate ?? { id: createId(), name: '', columns: [] }),
                    name: event.target.value,
                  },
                })}
              />

              <Stack spacing={2}>
                {(configuration.bankTemplate?.columns ?? []).map((column, index) => (
                  <Grid key={column.id} container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField label="Column Name" fullWidth value={column.columnName} onChange={(event) => updateConfiguration({
                        ...configuration,
                        bankTemplate: {
                          ...(configuration.bankTemplate ?? { id: createId(), name: '', columns: [] }),
                          columns: (configuration.bankTemplate?.columns ?? []).map((item, itemIndex) => itemIndex === index ? { ...item, columnName: event.target.value } : item),
                        },
                      })} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField label="Source Key" fullWidth value={column.sourceKey} onChange={(event) => updateConfiguration({
                        ...configuration,
                        bankTemplate: {
                          ...(configuration.bankTemplate ?? { id: createId(), name: '', columns: [] }),
                          columns: (configuration.bankTemplate?.columns ?? []).map((item, itemIndex) => itemIndex === index ? { ...item, sourceKey: event.target.value } : item),
                        },
                      })} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <TextField
                        select
                        label="Source Type"
                        fullWidth
                        value={column.sourceType}
                        onChange={(event) => updateConfiguration({
                          ...configuration,
                          bankTemplate: {
                            ...(configuration.bankTemplate ?? { id: createId(), name: '', columns: [] }),
                            columns: (configuration.bankTemplate?.columns ?? []).map((item, itemIndex) => itemIndex === index ? { ...item, sourceType: Number(event.target.value) as BankTemplateColumnSourceType } : item),
                          },
                        })}
                      >
                        <MenuItem value={BankTemplateColumnSourceType.System}>{BankTemplateColumnSourceTypeLabels[BankTemplateColumnSourceType.System]}</MenuItem>
                        <MenuItem value={BankTemplateColumnSourceType.Custom}>{BankTemplateColumnSourceTypeLabels[BankTemplateColumnSourceType.Custom]}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <TextField label="Format" fullWidth value={column.format ?? ''} onChange={(event) => updateConfiguration({
                        ...configuration,
                        bankTemplate: {
                          ...(configuration.bankTemplate ?? { id: createId(), name: '', columns: [] }),
                          columns: (configuration.bankTemplate?.columns ?? []).map((item, itemIndex) => itemIndex === index ? { ...item, format: event.target.value } : item),
                        },
                      })} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={column.isRequired}
                            onChange={(event) => updateConfiguration({
                              ...configuration,
                              bankTemplate: {
                                ...(configuration.bankTemplate ?? { id: createId(), name: '', columns: [] }),
                                columns: (configuration.bankTemplate?.columns ?? []).map((item, itemIndex) => itemIndex === index ? { ...item, isRequired: event.target.checked } : item),
                              },
                            })}
                          />
                        }
                        label=""
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 1 }}>
                      <IconButton color="error" onClick={() => updateConfiguration({
                        ...configuration,
                        bankTemplate: {
                          ...(configuration.bankTemplate ?? { id: createId(), name: '', columns: [] }),
                          columns: (configuration.bankTemplate?.columns ?? []).filter((_, itemIndex) => itemIndex !== index),
                        },
                      })}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Stack>

              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveBankTemplate} disabled={saving === 'bank'} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
                {saving === 'bank' ? 'Saving...' : 'Save Bank Template'}
              </Button>
            </Stack>
          </Paper>
        )}

        {tab === 'module' && (
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Typography variant="h6">Payroll Configuration Module</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControlLabel
                    control={<Switch checked={configuration.moduleConfiguration.autoGeneratePayslip} onChange={(event) => updateConfiguration({ ...configuration, moduleConfiguration: { ...configuration.moduleConfiguration, autoGeneratePayslip: event.target.checked } })} />}
                    label="Auto Generate Payslip"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControlLabel
                    control={<Switch checked={configuration.moduleConfiguration.enableBankAdviceExport} onChange={(event) => updateConfiguration({ ...configuration, moduleConfiguration: { ...configuration.moduleConfiguration, enableBankAdviceExport: event.target.checked } })} />}
                    label="Enable Bank Advice Export"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControlLabel
                    control={<Switch checked={configuration.moduleConfiguration.enableProration} onChange={(event) => updateConfiguration({ ...configuration, moduleConfiguration: { ...configuration.moduleConfiguration, enableProration: event.target.checked } })} />}
                    label="Attendance Based Proration"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    select
                    label="Proration Basis"
                    fullWidth
                    value={configuration.moduleConfiguration.prorationBasis ?? PayrollProrationBasis.CalendarDays}
                    onChange={(event) => updateConfiguration({
                      ...configuration,
                      moduleConfiguration: {
                        ...configuration.moduleConfiguration,
                        prorationBasis: Number(event.target.value) as PayrollProrationBasis,
                      },
                    })}
                    disabled={!configuration.moduleConfiguration.enableProration}
                    helperText="Calendar days deducts only LOP."
                  >
                    <MenuItem value={PayrollProrationBasis.CalendarDays}>
                      {PayrollProrationBasisLabels[PayrollProrationBasis.CalendarDays]}
                    </MenuItem>
                    <MenuItem value={PayrollProrationBasis.ScheduledWorkingDays}>
                      {PayrollProrationBasisLabels[PayrollProrationBasis.ScheduledWorkingDays]}
                    </MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControlLabel
                    control={<Switch checked={configuration.moduleConfiguration.lockPayslipAfterPayroll} onChange={(event) => updateConfiguration({ ...configuration, moduleConfiguration: { ...configuration.moduleConfiguration, lockPayslipAfterPayroll: event.target.checked } })} />}
                    label="Lock Payslip After Payroll"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    select
                    label="Default Payslip Template"
                    fullWidth
                    value={configuration.moduleConfiguration.defaultPayslipTemplateId ?? ''}
                    onChange={(event) => updateConfiguration({ ...configuration, moduleConfiguration: { ...configuration.moduleConfiguration, defaultPayslipTemplateId: event.target.value || null } })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {activeTemplateOptions.map((template) => (
                      <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    label="OT Hourly Rate"
                    type="number"
                    fullWidth
                    value={configuration.moduleConfiguration.overtimeHourlyRate ?? 0}
                    onChange={(event) => updateConfiguration({
                      ...configuration,
                      moduleConfiguration: {
                        ...configuration.moduleConfiguration,
                        overtimeHourlyRate: Math.max(0, Number(event.target.value) || 0),
                      },
                    })}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    label="OT Multiplier"
                    type="number"
                    fullWidth
                    value={configuration.moduleConfiguration.overtimeMultiplier ?? 1}
                    onChange={(event) => updateConfiguration({
                      ...configuration,
                      moduleConfiguration: {
                        ...configuration.moduleConfiguration,
                        overtimeMultiplier: Math.max(0, Number(event.target.value) || 0),
                      },
                    })}
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Default Bank Template Id"
                    fullWidth
                    value={configuration.moduleConfiguration.defaultBankTemplateId ?? configuration.bankTemplate?.id ?? ''}
                    onChange={(event) => updateConfiguration({ ...configuration, moduleConfiguration: { ...configuration.moduleConfiguration, defaultBankTemplateId: event.target.value || null } })}
                    helperText="Use the saved bank template id or leave blank."
                  />
                </Grid>
              </Grid>

              <Button variant="contained" startIcon={<SaveIcon />} onClick={saveModuleConfiguration} disabled={saving === 'module'} sx={{ alignSelf: 'flex-start' , textTransform: 'none' }}>
                {saving === 'module' ? 'Saving...' : 'Save Module Configuration'}
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
};

export default PayrollSettingsPage;
